import type React from 'react';
import { useRef, useState } from 'react';
import { Divider } from '@mui/material';
import clsx from 'clsx';

import { IconButton } from '@components/button/Button';
import Icon from '@components/icon/Icon';
import Tooltip from '@components/tooltip/Tooltip';
import { deriveDarkColor } from '@features/lead-management/utils/common';
import useOnClickOutside from '@utils/hooks';

import theme from '../../../themes/colors';

export interface SelectableItem {
  id: string;
  name: string;
  color?: string;
}

interface ItemSelectorProps<T extends SelectableItem> {
  isOpen: boolean;
  onClose: () => void;
  onSelectItem: (item: T) => void;
  onCreateItem: (itemName: string, color?: string) => void;
  onEditItem?: (item: T) => void;
  onUpdateItem?: (newItem: T) => void;
  selectedItems?: T[];
  className?: string;
  searchValue?: string;
  itemOptions?: T[];
  showColorSelector?: boolean;
  showEditButton?: boolean;
  createButtonText?: string;
  noItemsText?: string;
  EditModalComponent?: React.ComponentType<any>;
  selectedColor?: string;
  onColorChange?: (color: string) => void;
  itemType?: string;
  createButtonClassName?: string;
  itemRowClassName?: string;
  showItemDividers?: boolean;
  positionFixed?: boolean;
  getAnchorRect?: () => DOMRect | null;
  alwaysShowColorPill?: boolean;
  labelName?: string;
  nonEditableFields?: string[];
  showSelectionTooltip?: boolean;
}

const ItemSelector = <T extends SelectableItem>({
  isOpen,
  onClose,
  onSelectItem,
  onCreateItem,
  onEditItem,
  onUpdateItem,
  selectedItems = [],
  className = '',
  searchValue = '',
  itemOptions = [],
  showColorSelector = true,
  showEditButton = true,
  createButtonText = 'Create',
  noItemsText = 'No items found',
  EditModalComponent,
  selectedColor = '#F2E1E9',
  onColorChange,
  itemType = 'item',
  createButtonClassName,
  itemRowClassName,
  showItemDividers = true,
  positionFixed = false,
  getAnchorRect,
  alwaysShowColorPill = false,
  labelName,
  nonEditableFields,
  showSelectionTooltip
}: ItemSelectorProps<T>) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<
    { top: number; left: number } | undefined
  >();

  const filteredOptions = searchValue
    ? itemOptions?.filter((item) =>
        item.name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : itemOptions;

  const searchValueExists = itemOptions?.some(
    (item) => item.name.toLowerCase() === searchValue.toLowerCase()
  );

  useOnClickOutside(dropdownRef, (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-edit-modal]')) {
      return;
    }
    if (isOpen && onClose) {
      e.stopPropagation();
      onClose();
    }
  });

  const handleItemClick = (item: T) => {
    onSelectItem(item);
  };

  const handleCreateClick = (itemName?: string, color?: string) => {
    const nameToCreate = itemName || searchValue;
    if (nameToCreate && nameToCreate.trim()) {
      onCreateItem(nameToCreate.trim(), color);
    }
    onClose();
  };

  const handleEditClick = (item: T, event?: React.MouseEvent) => {
    if (!showEditButton || !EditModalComponent) return;

    // Calculate the actual position of the clicked element
    const target = event?.currentTarget as HTMLElement;
    if (target) {
      const rect = target.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top,
        left: rect.left
      });
    } else {
      // Fallback to dropdown position if event target is not available
      const dropdownRect = dropdownRef.current?.getBoundingClientRect();
      if (dropdownRect) {
        setDropdownPosition({
          top: dropdownRect.top,
          left: dropdownRect.left
        });
      }
    }

    setEditingItem(item);
    setEditModalOpen(true);
    onEditItem?.(item);
  };

  const handleSaveItem = (updatedItem: T) => {
    if (onUpdateItem && editingItem) {
      onUpdateItem(updatedItem);
    }

    // Update the selected color when a color is chosen
    if (updatedItem.color && onColorChange) {
      onColorChange(updatedItem.color);
    }

    setEditModalOpen(false);
    setEditingItem(null);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingItem(null);
    setDropdownPosition(undefined);
  };

  const renderCreateButtonWithColorAndSearchValue = () => {
    if (searchValue && searchValue.trim()) {
      return (
        <button
          type="button"
          className={clsx(
            'mt-1 flex cursor-pointer items-center justify-between rounded-xl bg-grey10 px-4 py-1 hover:bg-grey20 focus:outline-none',
            createButtonClassName
          )}
          onClick={() =>
            handleCreateClick(
              searchValue.trim(),
              showColorSelector ? selectedColor : undefined
            )
          }
          tabIndex={0}
          aria-label={createButtonText}
        >
          <div className="flex items-center gap-2">
            <div className="flex size-3.5 items-center justify-center">
              <Icon
                name="plus"
                className="size-2 text-black"
                stroke="black"
                fill="black"
              />
            </div>
            <div className="text-sm font-normal text-darkGrey">
              {createButtonText}
            </div>
            {(showColorSelector || alwaysShowColorPill) && (
              <div className="flex items-center gap-2">
                <div
                  className="rounded-2xl px-4 py-1.5 text-sm font-normal"
                  style={{
                    color: theme.darkGrey,
                    backgroundColor: selectedColor
                  }}
                >
                  {searchValue}
                </div>
              </div>
            )}
            {!showColorSelector && !alwaysShowColorPill && (
              <div className="flex items-center gap-2">
                <div className="rounded-2xl px-4 py-1.5 text-sm font-normal">
                  {searchValue}
                </div>
              </div>
            )}
          </div>
        </button>
      );
    }
    return null;
  };

  if (!isOpen) return null;

  const anchorRect = positionFixed && getAnchorRect ? getAnchorRect() : null;

  const showEditIcon = (name: string) =>
    showEditButton &&
    EditModalComponent &&
    (nonEditableFields?.length ? !nonEditableFields?.includes(name) : true);

  // Unified dropdown that handles both simple and complex cases
  return (
    <>
      <div
        ref={dropdownRef}
        className={clsx(
          positionFixed
            ? 'fixed z-50 max-h-60 rounded-lg bg-white shadow-lg'
            : 'absolute z-50 mt-1 max-h-60 w-full rounded-lg bg-white shadow-lg',
          className
        )}
        style={
          positionFixed && anchorRect
            ? {
                top: anchorRect.bottom + 8,
                left: anchorRect.left,
                width: anchorRect.width
              }
            : undefined
        }
      >
        <div
          className={clsx(
            'flex h-full flex-col gap-0.5 overflow-y-auto bg-theme px-2',
            positionFixed
              ? 'rounded-xl border-none shadow-lg'
              : ' rounded-md border border-grey40 shadow-sm'
          )}
        >
          {/* Create button with search  and color fields */}
          {renderCreateButtonWithColorAndSearchValue()}

          {/* Dropdown items */}
          <div className="flex max-h-40 w-full flex-col gap-1">
            {filteredOptions?.map((item, index) => {
              const isSelected = selectedItems.some(
                (selected) => selected.id === item.id
              );

              return (
                <div key={item.id}>
                  <button
                    type="button"
                    className={clsx(
                      'mx-2 my-1 flex min-h-9 w-full items-center justify-between rounded-lg py-2 pl-3 pr-4 text-sm focus:outline-none',
                      isSelected ? 'bg-blue4' : 'hover:bg-grey10',
                      'cursor-pointer',
                      itemRowClassName
                    )}
                    onClick={() => handleItemClick(item)}
                    tabIndex={0}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {showColorSelector || alwaysShowColorPill ? (
                        <div
                          className="flex max-w-full items-center gap-2 rounded-3xl px-3 py-1.5"
                          style={{
                            backgroundColor: item.color || selectedColor,
                            color: deriveDarkColor(item.color || selectedColor)
                          }}
                          data-tooltip-id={`tooltip-${item.id}`}
                        >
                          {isSelected && (
                            <Icon
                              name="check-mark"
                              size="12"
                              stroke={theme.primary}
                            />
                          )}
                          <div className="truncate text-sm font-normal">
                            {item.name}
                          </div>
                        </div>
                      ) : (
                        <div className="truncate text-sm font-normal text-darkGrey">
                          {item.name}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      {showEditIcon(item.name) && (
                        <IconButton
                          iconName="edit-grey"
                          variant="transparent"
                          className="size-6 !h-7 !p-0"
                          buttonSize="small"
                          iconSize="medium"
                          handleClick={(
                            event: React.MouseEvent<HTMLButtonElement>
                          ) => {
                            event.stopPropagation();
                            handleEditClick(item, event);
                          }}
                        />
                      )}
                    </div>
                  </button>
                  {showItemDividers && index + 1 !== filteredOptions.length && (
                    <Divider className="border-grey40" />
                  )}
                  {showSelectionTooltip && isSelected && (
                    <Tooltip
                      id={`tooltip-${item.id}`}
                      type="text"
                      tooltipText="Already added"
                      place="right"
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* No items found text */}
          {searchValue &&
            filteredOptions?.length === 0 &&
            !searchValueExists && (
              <div className="p-4 text-sm text-grey40">
                {noItemsText} matching &quot;{searchValue}&quot;
              </div>
            )}

          {itemOptions.length === 0 && !searchValue && (
            <div className="px-4 py-6 text-sm text-grey40">
              {noItemsText}. Type to create a new {itemType}.
            </div>
          )}
        </div>
      </div>

      {EditModalComponent && (
        <EditModalComponent
          isOpen={editModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveItem}
          editingItem={editingItem}
          dropdownPosition={dropdownPosition}
          showColorSelector={showColorSelector}
          labelName={labelName}
          style={{
            left: dropdownPosition
              ? `${dropdownPosition.left + 520}px`
              : '520px',
            top: dropdownPosition ? `${dropdownPosition.top}px` : '0px'
          }}
        />
      )}
    </>
  );
};

export default ItemSelector;
